/**
 * Clinical Reference Service
 * 
 * Manages clinical reference PDFs — upload, tag, store, query, match.
 * References are whole PDFs linked to ChartMind items via clinical tags.
 * Separate from the RAG/embedding pipeline (documentProcessingService).
 */

import { storage, db, firebaseApp } from '@healthdesk/shared-services';
import firebase from 'firebase/compat/app';
import { debugLog } from '../../utils/debugLog';

const COLLECTION = 'clinicalReferences';
const STORAGE_PATH = 'clinical-references';

/**
 * Roles that may read `clinicalReferences` per firebase/firestore.rules.
 * Prefetch only for these to avoid permission-denied noise for everyone else.
 */
export function shouldPrefetchClinicalReferences(role) {
  if (!role || typeof role !== 'string') return false;
  return (
    role === 'llm-manager-global' ||
    role === 'llm-manager-regional' ||
    role === 'chartmind-admin' ||
    role === 'chartmind-kijabe' ||
    role === 'professional-chartmind' ||
    role === 'pro2'
  );
}

// File limits
const MAX_SINGLE_PDF = 50 * 1024 * 1024; // 50MB per individual PDF

function isPermissionDeniedError(err) {
  const code = err?.code || "";
  const message = String(err?.message || "").toLowerCase();
  return code === "permission-denied" || message.includes("permission") || message.includes("insufficient permissions");
}

/**
 * Upload a single clinical reference PDF.
 * Extracts text and auto-generates clinical tags via Cloud Function.
 * @param {File} file - PDF file
 * @param {Object} metadata - { source, author, publicationDate, documentType, specialty }
 * @param {string} userId - Uploading user ID
 * @param {Object} options - { adminId, baseQueryId, position }
 * @returns {Promise<Object>} Created reference document
 */
export async function uploadReference(file, metadata, userId, options = {}) {
  if (file.size > MAX_SINGLE_PDF) {
    throw new Error(`File size exceeds limit of ${MAX_SINGLE_PDF / 1024 / 1024}MB`);
  }

  // Step 1: Upload to temp storage for text extraction
  const tempRef = storage.ref(`temp/${Date.now()}_${file.name}`);
  await tempRef.put(file);
  const tempURL = await tempRef.getDownloadURL();

  // Step 2: Extract text and clinical tags via Cloud Function
  const extractTextFunction = firebaseApp.functions().httpsCallable('extractTextFromFile', { timeout: 120000 });
  let clinicalTags = null;
  let tagSearchIndex = [];
  let primaryTopics = [];
  
  try {
    const result = await extractTextFunction({
      fileURL: tempURL,
      fileType: file.type || 'application/pdf',
      preserveStructure: true,
      extractTags: true,
    });
    clinicalTags = result.data.clinicalTags || null;
    tagSearchIndex = result.data.tagSearchIndex || [];
    primaryTopics = result.data.primaryTopics || [];
  } catch (error) {
    console.warn('[ClinicalReference] Tag extraction failed, continuing without tags:', error.message);
  }

  // Clean up temp file
  try { await tempRef.delete(); } catch (e) { /* ignore */ }

  // Step 3: Upload PDF to permanent storage
  const filePath = `${STORAGE_PATH}/${options.adminId || 'global'}/${Date.now()}_${file.name}`;
  const fileRef = storage.ref(filePath);
  await fileRef.put(file);
  const downloadURL = await fileRef.getDownloadURL();

  // Step 4: Store reference metadata in Firestore
  const docRef = db.collection(COLLECTION).doc();
  const referenceData = {
    id: docRef.id,
    fileName: file.name,
    fileSize: file.size,
    filePath,
    downloadURL,
    source: metadata.source || '',
    author: metadata.author || '',
    publicationDate: metadata.publicationDate || null,
    documentType: metadata.documentType || 'reference',
    specialty: metadata.specialty || '',
    tags: clinicalTags || { symptoms: [], diagnoses: [], tests: [], treatments: [], scoringSystems: [], general: [] },
    tagSearchIndex: tagSearchIndex,
    primaryTopics: primaryTopics,
    adminId: options.adminId || null,
    userId,
    position: options.position || 3,
    baseQueryId: options.baseQueryId || null,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  await docRef.set(referenceData);

  return { id: docRef.id, ...referenceData, clinicalTags, tagSearchIndex };
}

/**
 * Get all references for a given admin.
 * @param {Object} options - { adminId }
 * @returns {Promise<Array>} Array of reference documents
 */
export async function getReferencesForAdmin(options = {}) {
  let query = db.collection(COLLECTION);

  if (options.adminId) {
    query = query.where('adminId', '==', options.adminId);
  }

  const snapshot = await query.orderBy('createdAt', 'desc').get();

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate() || null,
    updatedAt: doc.data().updatedAt?.toDate() || null,
  }));
}

/**
 * Get references scoped to a query: refs where baseQueryId === queryId OR baseQueryId is null (global).
 * Use this in the chatbot and in the Clinical References tab when editing a specific query.
 * @param {string} queryId - Base query ID (e.g. ama-kijabe-1)
 * @returns {Promise<Array>} Array of reference documents (merged, sorted by createdAt desc)
 */
export async function getReferencesForQuery(queryId) {
  if (!queryId || typeof queryId !== 'string') return [];

  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    console.warn('[ClinicalReference] No authenticated user, skipping getReferencesForQuery');
    return [];
  }
  if (currentUser.isAnonymous) {
    return [];
  }

  try {
    const coll = db.collection(COLLECTION);
    const [snapQuery, snapGlobal] = await Promise.all([
      coll.where('baseQueryId', '==', queryId).orderBy('createdAt', 'desc').get(),
      coll.where('baseQueryId', '==', null).orderBy('createdAt', 'desc').get(),
    ]);

    const byId = new Map();
    const mapDoc = (doc) => {
      const d = doc.data();
      const out = { id: doc.id, ...d, createdAt: d.createdAt?.toDate?.() ?? null, updatedAt: d.updatedAt?.toDate?.() ?? null };
      if (!byId.has(doc.id)) byId.set(doc.id, out);
      return out;
    };
    snapQuery.docs.forEach(mapDoc);
    snapGlobal.docs.forEach(mapDoc);
    const list = Array.from(byId.values());
    list.sort((a, b) => (b.createdAt && a.createdAt ? b.createdAt.getTime() - a.createdAt.getTime() : 0));
    return list;
  } catch (err) {
    if (isPermissionDeniedError(err)) {
      debugLog(
        '[ClinicalReference] Query references unavailable for current user scope; returning empty references list.'
      );
      return [];
    }
    throw err;
  }
}

/**
 * Get all references (for session caching in the matching hook).
 * Returns all references accessible to the current user.
 * @param {Object} options - { adminId }
 * @returns {Promise<Array>} Array of reference documents
 */
export async function getAllReferences(options = {}) {
  // Guard: ensure Firebase Auth user is ready before querying
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    console.warn('[ClinicalReference] No authenticated user, skipping query');
    return [];
  }
  if (currentUser.isAnonymous) {
    return [];
  }

  try {
    // Fetch all references — Firestore rules will filter by permissions
    const snapshot = await db.collection(COLLECTION)
      .orderBy('createdAt', 'desc')
      .get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || null,
      updatedAt: doc.data().updatedAt?.toDate() || null,
    }));
  } catch (err) {
    // Expected for roles not in shouldPrefetchClinicalReferences; dev-only trace.
    if (isPermissionDeniedError(err)) {
      debugLog(
        '[ClinicalReference] Clinical references not readable for this user; continuing without references.'
      );
      return [];
    }
    throw err;
  }
}

/** In-memory cache so refs are ready when the chatbot response arrives. Keyed by uid so we don't serve refs across users. */
let refsCache = { uid: null, refs: null, promise: null };

/** Per-query cache for chatbot: key is `${uid}:${queryId}`. */
let refsByQueryCache = { key: null, refs: null, promise: null };

/**
 * Get clinical references, using cache when available so the Source documents list can show immediately after the LLM response.
 * Call this instead of getAllReferences() from the chatbot so repeat visits and prefetched refs avoid delay.
 * @returns {Promise<Array>} Array of reference documents
 */
export function getCachedOrLoadReferences() {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser || currentUser.isAnonymous) {
    refsCache = { uid: null, refs: null, promise: null };
    return Promise.resolve([]);
  }
  if (refsCache.uid !== currentUser.uid) {
    refsCache = { uid: currentUser.uid, refs: null, promise: null };
  }
  if (refsCache.refs !== null) {
    return Promise.resolve(refsCache.refs);
  }
  if (refsCache.promise) {
    return refsCache.promise;
  }
  refsCache.promise = getAllReferences()
    .then((refs) => {
      refsCache.refs = refs || [];
      refsCache.promise = null;
      return refsCache.refs;
    })
    .catch((err) => {
      refsCache.promise = null;
      throw err;
    });
  return refsCache.promise;
}

/**
 * Get clinical references for a specific query (refs linked to this query + global refs), with cache.
 * Use in the chatbot when queryId is known so only refs from the Clinical References tab for this query are used.
 * @param {string} queryId - Base query ID from the route (e.g. ama-kijabe-1)
 * @returns {Promise<Array>} Array of reference documents
 */
export function getCachedOrLoadReferencesForQuery(queryId) {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser || currentUser.isAnonymous || !queryId) {
    return Promise.resolve([]);
  }
  const key = `${currentUser.uid}:${queryId}`;
  if (refsByQueryCache.key === key && refsByQueryCache.refs !== null) {
    return Promise.resolve(refsByQueryCache.refs);
  }
  if (refsByQueryCache.key === key && refsByQueryCache.promise) {
    return refsByQueryCache.promise;
  }
  refsByQueryCache.key = key;
  refsByQueryCache.refs = null;
  refsByQueryCache.promise = getReferencesForQuery(queryId)
    .then((refs) => {
      refsByQueryCache.refs = refs || [];
      refsByQueryCache.promise = null;
      return refsByQueryCache.refs;
    })
    .catch((err) => {
      refsByQueryCache.promise = null;
      throw err;
    });
  return refsByQueryCache.promise;
}

/**
 * Start loading clinical references in the background so they are ready when the user opens the chatbot or gets a response.
 * Safe to call when not authenticated (no-op). Call once when the user is in the app (e.g. after login).
 */
export function prefetchClinicalReferences() {
  getCachedOrLoadReferences().catch(() => {});
}

/**
 * Clear in-memory ref caches (all-refs and per-query). Call after upload/update/delete so next load is fresh.
 */
export function invalidateRefsCache() {
  refsCache = { uid: null, refs: null, promise: null };
  refsByQueryCache = { key: null, refs: null, promise: null };
}

/**
 * Check if `needle` appears at a word boundary within `haystack`.
 * Word boundaries: start/end of string, whitespace, hyphens, underscores, slashes, parentheses, commas, semicolons, colons, periods.
 * Underscore included so "stroke" matches in "11052025_Stroke.pdf".
 * Checks all occurrences of needle in haystack — returns true if any is word-boundary-aligned.
 */
function isWordBoundaryMatch(haystack, needle) {
  const isBoundaryChar = (ch) => !ch || /[\s\-_/(),;:.]/.test(ch);
  let idx = haystack.indexOf(needle);
  while (idx !== -1) {
    const before = idx === 0 ? '' : haystack[idx - 1];
    const after = idx + needle.length >= haystack.length ? '' : haystack[idx + needle.length];
    if (isBoundaryChar(before) && isBoundaryChar(after)) return true;
    idx = haystack.indexOf(needle, idx + 1);
  }
  return false;
}

/** Maximum number of reference links to show per matched term */
const MAX_REFS_PER_TERM = 2;

/** Max refs to return for "by title" so one common term doesn't list every document */
const MAX_REFS_BY_TITLE = 8;

/**
 * Find references that match search terms in document title (fileName/source) OR in primary topics only.
 * We never count a match when the term is exactly the ref's source (e.g. "Kijabe").
 * Tag matching uses only primaryTopics so refs that merely mention a term don't surface.
 * Legacy refs without primaryTopics fall back to full tagSearchIndex.
 * @param {Array<string>} searchTerms - Normalized terms (e.g. from user query + reply)
 * @param {Array<Object>} cachedReferences - Pre-fetched references
 * @returns {Array<Object>} Refs with id, fileName, downloadURL, source, matchedTag (matched term for display)
 */
export function findReferencesByTitle(searchTerms, cachedReferences) {
  if (!searchTerms?.length || !cachedReferences?.length) return [];

  const normalizedTerms = searchTerms
    .map(t => t.toLowerCase().trim())
    .filter(Boolean)
    .filter(t => t.length >= 3);
  normalizedTerms.sort((a, b) => b.length - a.length);
  const byRefId = new Map();

  for (const term of normalizedTerms) {
    for (const ref of cachedReferences) {
      const title = `${ref.fileName || ''} ${ref.source || ''}`.trim().toLowerCase();
      const refSourceLower = (ref.source || '').toLowerCase().trim();
      if (term === refSourceLower) continue;
      // Use primary topics only for tag matching; legacy refs without primaryTopics use full tagSearchIndex
      const primaryTopics = (ref.primaryTopics || []).map(t => String(t).toLowerCase().trim());
      const tagsForMatch = primaryTopics.length > 0 ? primaryTopics : (ref.tagSearchIndex || []).map(t => String(t).toLowerCase());
      const searchable = `${title} ${tagsForMatch.join(' ')}`.toLowerCase();
      if (!searchable.trim()) continue;
      if (!isWordBoundaryMatch(searchable, term)) continue;
      if (byRefId.has(ref.id)) continue;
      byRefId.set(ref.id, {
        id: ref.id,
        fileName: ref.fileName,
        downloadURL: ref.downloadURL,
        source: ref.source,
        documentType: ref.documentType,
        specialty: ref.specialty,
        matchedTag: term,
      });
    }
  }

  const out = Array.from(byRefId.values());
  return out.length > MAX_REFS_BY_TITLE ? out.slice(0, MAX_REFS_BY_TITLE) : out;
}

/**
 * Find references matching an array of clinical terms.
 * Uses ONLY primary topics for tag matching so refs that merely mention a term don't surface.
 * Legacy refs without primaryTopics fall back to full tagSearchIndex.
 *
 * Matching tiers:
 *   1. Exact match (topic === term): score 100
 *   2. Topic is a word-boundary phrase within term: topic.length >= 6 AND >= 35% of term length
 *   3. Term is a word-boundary phrase within topic: term.length >= 3 AND >= 40% of topic length
 *
 * Results are scored, sorted, and capped at MAX_REFS_PER_TERM per term.
 *
 * @param {Array<string>} searchTerms - Array of clinical terms to match
 * @param {Array<Object>} cachedReferences - Pre-fetched references to search against
 * @returns {Object} Map of term → array of matching references (each with matchedTag, matchScore)
 */
export function findMatchingReferences(searchTerms, cachedReferences) {
  const matchMap = {};

  if (!searchTerms || !cachedReferences || cachedReferences.length === 0) {
    return matchMap;
  }

  const normalizedTerms = searchTerms.map(t => t.toLowerCase().trim()).filter(Boolean);

  debugLog('[ClinicalRefMatch] Matching', normalizedTerms.length, 'terms against', cachedReferences.length, 'refs (primary topics only)');

  for (const term of normalizedTerms) {
    const matches = [];

    for (const ref of cachedReferences) {
      // Use primary topics only; legacy refs without primaryTopics use full tagSearchIndex
      const primaryTopics = (ref.primaryTopics || []).map(t => String(t).toLowerCase().trim());
      const tagsToMatch = primaryTopics.length > 0 ? primaryTopics : (ref.tagSearchIndex || []).map(t => String(t).toLowerCase().trim());
      let bestTag = null;
      let bestScore = 0;

      for (const tag of tagsToMatch) {
        let score = 0;

        if (tag === term) {
          score = 100;
        } else if (term.includes(tag) && tag.length >= 6 && isWordBoundaryMatch(term, tag)) {
          const coverage = tag.length / term.length;
          if (coverage >= 0.35) {
            score = 50 + Math.round(coverage * 40);
          }
        } else if (tag.includes(term) && term.length >= 3 && isWordBoundaryMatch(tag, term)) {
          const coverage = term.length / tag.length;
          if (coverage >= 0.40) {
            score = 40 + Math.round(coverage * 30);
          }
        }

        if (score > bestScore) {
          bestScore = score;
          bestTag = tag;
        }
      }

      if (bestScore > 0 && bestTag) {
        matches.push({
          id: ref.id,
          fileName: ref.fileName,
          downloadURL: ref.downloadURL,
          source: ref.source,
          documentType: ref.documentType,
          specialty: ref.specialty,
          matchedTag: bestTag,
          matchScore: bestScore,
        });
      }
    }

    if (matches.length > 0) {
      matches.sort((a, b) => b.matchScore - a.matchScore);
      const topMatches = matches.slice(0, MAX_REFS_PER_TERM);

      const originalTerm = searchTerms.find(t => t.toLowerCase().trim() === term);
      matchMap[originalTerm || term] = topMatches;
    }
  }

  const matchCount = Object.keys(matchMap).length;
  debugLog('[ClinicalRefMatch] Result:', matchCount, 'terms matched,',
    Object.values(matchMap).reduce((s, a) => s + a.length, 0), 'total links');
  if (matchCount > 0) {
    debugLog('[ClinicalRefMatch] Matched terms:', Object.keys(matchMap).map(t => {
      const refs = matchMap[t];
      return `${t} (${refs.length} refs, best: "${refs[0]?.matchedTag}" @${refs[0]?.matchScore})`;
    }));
  }

  return matchMap;
}

/**
 * Update tags for a reference document.
 * @param {string} referenceId - Document ID
 * @param {Object} tags - Updated tags object
 * @returns {Promise<void>}
 */
export async function updateTags(referenceId, tags) {
  // Rebuild flattened search index
  const tagSearchIndex = [...new Set(
    Object.values(tags).flat().map(t => String(t).toLowerCase().trim()).filter(Boolean)
  )];

  await db.collection(COLLECTION).doc(referenceId).update({
    tags,
    tagSearchIndex,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

/**
 * Delete a reference and its Storage file.
 * @param {string} referenceId - Document ID
 * @returns {Promise<void>}
 */
export async function deleteReference(referenceId) {
  const doc = await db.collection(COLLECTION).doc(referenceId).get();
  if (!doc.exists) {
    throw new Error('Reference not found');
  }

  const data = doc.data();

  // Delete file from Storage
  if (data.filePath) {
    try {
      await storage.ref(data.filePath).delete();
    } catch (e) {
      console.warn('[ClinicalReference] Failed to delete storage file:', e.message);
    }
  }

  // Delete Firestore document
  await db.collection(COLLECTION).doc(referenceId).delete();
}

/**
 * Update source metadata for a reference.
 * @param {string} referenceId - Document ID
 * @param {Object} metadata - Updated metadata fields
 * @returns {Promise<void>}
 */
export async function updateMetadata(referenceId, metadata) {
  const updates = { updatedAt: firebase.firestore.FieldValue.serverTimestamp() };
  
  if (metadata.source !== undefined) updates.source = metadata.source;
  if (metadata.author !== undefined) updates.author = metadata.author;
  if (metadata.publicationDate !== undefined) updates.publicationDate = metadata.publicationDate;
  if (metadata.documentType !== undefined) updates.documentType = metadata.documentType;
  if (metadata.specialty !== undefined) updates.specialty = metadata.specialty;

  await db.collection(COLLECTION).doc(referenceId).update(updates);
}

const clinicalReferenceService = {
  uploadReference,
  getReferencesForAdmin,
  getAllReferences,
  getCachedOrLoadReferences,
  shouldPrefetchClinicalReferences,
  prefetchClinicalReferences,
  findMatchingReferences,
  findReferencesByTitle,
  updateTags,
  deleteReference,
  updateMetadata,
};

export default clinicalReferenceService;
